import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { formatDate } from "@/lib/demo-data";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Search,
  AlertCircle,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  FileJson,
  Sparkles,
  Check,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Keyboard,
  Zap,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  Info,
  RotateCcw,
  Ban,
  Building2,
  User as UserIcon,
  BarChart3,
  UserCheck,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ZoneMultiSelect } from "@/components/ZoneMultiSelect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const QUALIFICATION_STATUSES = ["NEW", "AUTO_QUALIFIED", "NEEDS_REVIEW", "APPROVED", "REJECTED"] as const;

const QUALIFICATION_STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  NEW: { label: "Nouveau", variant: "outline" },
  AUTO_QUALIFIED: { label: "Auto-validé", variant: "secondary" },
  NEEDS_REVIEW: { label: "À revoir", variant: "outline" },
  IN_REVIEW: { label: "À revoir", variant: "outline" },
  APPROVED: { label: "Validé", variant: "default" },
  QUALIFIED: { label: "Validé", variant: "default" },
  REJECTED: { label: "Rejeté", variant: "destructive" },
};

type SortField = "score" | "deadline" | "createdAt" | "matches";

function SortableHead({
  field,
  label,
  sortField,
  sortDir,
  onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: "asc" | "desc";
  onSort: (field: SortField, dir: "asc" | "desc") => void;
}) {
  const isActive = sortField === field;
  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50"
      onClick={() => {
        if (isActive) {
          onSort(field, sortDir === "asc" ? "desc" : "asc");
        } else {
          onSort(field, "desc");
        }
      }}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

export default function Tenders() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [qualityFilter, setQualityFilter] = useState<"all" | "low">("all");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [selectedTender, setSelectedTender] = useState<string | null>(null);
  const [qualificationTab, setQualificationTab] = useState<"new" | "auto-qualified" | "needs-review" | "approved" | "rejected" | "invalid">("new");
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState<string | null>(null);
  const [matchDialogTenderId, setMatchDialogTenderId] = useState<string | null>(null);
  const [myAoOnly, setMyAoOnly] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const [reassignTenderId, setReassignTenderId] = useState<string | null>(null);
  const [reassignUserId, setReassignUserId] = useState<string>("");
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedTenderId, setExpandedTenderId] = useState<string | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [sortField, setSortField] = useState<"score" | "deadline" | "createdAt" | "matches">("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    buyer: "",
    description: "",
    location: "",
    region: "",
    deadlineAt: "",
    budgetMin: "",
    budgetMax: "",
    url: "",
    categoryIds: [] as string[],
    zones: "",
    isRelevantForSME: true,
  });

  const qualificationStatusFilter =
    qualificationTab === "new" || qualificationTab === "needs-review"
      ? "TO_QUALIFY" as const
      : qualificationTab === "auto-qualified" || qualificationTab === "approved"
        ? "QUALIFIED" as const
        : "REJECTED" as const;

  const { data, isLoading, isError, refetch } = trpc.admin.tenders.list.useQuery({
    page,
    limit: 20,
    search: search || undefined,
    qualityScoreMax: qualityFilter === "low" ? 60 : undefined,
    source: sourceFilter || undefined,
    qualificationStatus: qualificationStatusFilter,
  });

  const computeQualityMutation = trpc.admin.tenders.computeQualityScore.useMutation({
    onSuccess: () => {
      toast.success("Score de qualité calculé");
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const { data: tenderDetail, isLoading: tenderDetailLoading, isError: tenderDetailError, refetch: refetchTenderDetail } = trpc.admin.tenders.get.useQuery(
    { id: selectedTender! },
    { enabled: !!selectedTender }
  );

  const { data: qualification, isLoading: qualificationLoading, isError: qualificationError, refetch: refetchQualification } = trpc.admin.qualification.get.useQuery(
    { tenderId: selectedTender! },
    { enabled: !!selectedTender }
  );

  const { data: suggestions, isLoading: suggestionsLoading, isError: suggestionsError, refetch: refetchSuggestions } = trpc.admin.tenders.getSuggestions.useQuery(
    { tenderId: selectedTender! },
    { enabled: !!selectedTender }
  );

  const { data: categories } = trpc.admin.categories.list.useQuery({ active: true });

  const generateSuggestionsMutation = trpc.admin.tenders.generateSuggestions.useMutation({
    onSuccess: () => {
      toast.success("Suggestions générées");
      refetchSuggestions();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const qualifyInOneClickMutation = trpc.admin.qualification.qualifyInOneClick.useMutation({
    onSuccess: (result: { matchedCompanies: number; matchedCategories: number }) => {
      toast.success(`AO qualifié en 1 clic ! ${result.matchedCompanies} entreprises matchées`);
      refetchQualification();
      refetchTenderDetail();
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const saveQualificationMutation = trpc.admin.qualification.createOrUpdate.useMutation({
    onSuccess: () => {
      toast.success("Qualification enregistrée");
      refetchQualification();
      refetchTenderDetail();
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const markInvalidMutation = trpc.admin.tenders.markInvalid.useMutation({
    onSuccess: () => {
      toast.success("AO marqué comme invalide");
      refetch();
      if (selectedTender) setSelectedTender(null);
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const markValidMutation = trpc.admin.tenders.markValid.useMutation({
    onSuccess: () => {
      toast.success("AO marqué comme valide");
      refetch();
      if (selectedTender) setSelectedTender(null);
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const { data: staffList } = trpc.admin.staff.list.useQuery(undefined, {
    enabled: isSuperAdmin,
  });

  const { data: opsPerformance, refetch: refetchPerformance } = trpc.admin.qualification.getOpsPerformanceReport.useQuery(undefined, {
    enabled: isSuperAdmin && showPerformance,
  });

  const reassignMutation = trpc.admin.qualification.reassign.useMutation({
    onSuccess: () => {
      toast.success("AO réassigné avec succès");
      setReassignTenderId(null);
      setReassignUserId("");
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const { data: invalidData, isLoading: invalidLoading, isError: invalidError, refetch: refetchInvalid } = trpc.admin.tenders.listInvalid.useQuery(
    { page, limit: 20 },
    { enabled: qualificationTab === "invalid" }
  );

  const retryParsingMutation = trpc.admin.tenders.retryParsing.useMutation({
    onSuccess: () => {
      toast.success("Parsing relancé — l'AO est désormais valide");
      refetchInvalid();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const { data: matchDialogDetail, isLoading: matchDialogLoading } = trpc.admin.tenders.get.useQuery(
    { id: matchDialogTenderId! },
    { enabled: !!matchDialogTenderId }
  );

  const recalculateMatchesMutation = trpc.admin.tenders.rerunScoring.useMutation({
    onSuccess: () => {
      toast.success("Matches recalculés");
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const createTenderMutation = trpc.admin.tenders.create.useMutation({
    onSuccess: () => {
      toast.success("AO créé avec succès");
      setShowCreateDialog(false);
      setCreateForm({
        title: "", buyer: "", description: "", location: "", region: "",
        deadlineAt: "", budgetMin: "", budgetMax: "", url: "",
        categoryIds: [], zones: "", isRelevantForSME: true,
      });
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const downgradeMutation = trpc.admin.qualification.downgradeToNeedsReview.useMutation({
    onSuccess: () => {
      toast.success("AO rétrogradé vers « À revoir »");
      setShowDowngradeConfirm(null);
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const approveTenderMutation = trpc.admin.qualification.approveTender.useMutation({
    onSuccess: () => {
      toast.success("AO approuvé — matching lancé");
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const bulkQualifyMutation = trpc.admin.qualification.qualifyManyInOneClick.useMutation({
    onSuccess: (result: { qualified: number; skipped: number; errors: Array<{ tenderId: string; error: string }> }) => {
      toast.success(`${result.qualified} AO qualifiés, ${result.skipped} ignorés${result.errors.length > 0 ? `, ${result.errors.length} erreurs` : ""}`);
      setSelectedIds(new Set());
      setShowBulkConfirm(false);
      refetch();
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const ids = filteredTenders.map((t: any) => t.id);
    setSelectedIds((prev) => {
      const allSelected = ids.every((id: string) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  }, [data?.items]);

  const allItems = data?.items || [];
  const statusFiltered = qualificationTab === "new"
    ? allItems.filter((t: any) => !t.TenderQualification || t.TenderQualification.status === "NEW")
    : qualificationTab === "needs-review"
      ? allItems.filter((t: any) => t.TenderQualification?.status === "NEEDS_REVIEW" || t.TenderQualification?.status === "IN_REVIEW")
      : qualificationTab === "auto-qualified"
        ? allItems.filter((t: any) => t.TenderQualification?.status === "AUTO_QUALIFIED")
        : qualificationTab === "approved"
          ? allItems.filter((t: any) => t.TenderQualification?.status === "APPROVED")
          : allItems;
  const filteredTenders = (myAoOnly
    ? statusFiltered.filter((t: any) => t.TenderQualification?.assigneeUserId === user?.id)
    : statusFiltered
  ).sort((a: any, b: any) => {
    let aVal: number, bVal: number;
    switch (sortField) {
      case "score":
        aVal = a.qualityScore ?? 0;
        bVal = b.qualityScore ?? 0;
        break;
      case "deadline":
        aVal = a.deadlineAt ? new Date(a.deadlineAt).getTime() : 0;
        bVal = b.deadlineAt ? new Date(b.deadlineAt).getTime() : 0;
        break;
      case "createdAt":
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      case "matches":
        aVal = a._count?.TenderMatch ?? 0;
        bVal = b._count?.TenderMatch ?? 0;
        break;
      default:
        return 0;
    }
    return sortDir === "asc" ? aVal - bVal : bVal - aVal;
  });
  const totalFiltered = data?.total || 0;

  const newCount = allItems.filter((t: any) => !t.TenderQualification || t.TenderQualification.status === "NEW").length;
  const autoQualifiedCount = allItems.filter((t: any) => t.TenderQualification?.status === "AUTO_QUALIFIED").length;
  const needsReviewCount = allItems.filter((t: any) => t.TenderQualification?.status === "NEEDS_REVIEW" || t.TenderQualification?.status === "IN_REVIEW").length;
  const approvedCount = allItems.filter((t: any) => t.TenderQualification?.status === "APPROVED").length;

  const focusedTender = focusedIndex >= 0 && focusedIndex < filteredTenders.length
    ? filteredTenders[focusedIndex]
    : null;

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable) return;

      switch (e.key) {
        case "j":
        case "Enter":
          if (focusedTender && qualificationTab === "new") {
            e.preventDefault();
            qualifyInOneClickMutation.mutate({ tenderId: focusedTender.id });
          }
          break;
        case "r":
          if (focusedTender) {
            e.preventDefault();
            setExpandedTenderId(focusedTender.id);
            setSelectedTender(focusedTender.id);
          }
          break;
        case "s":
          if (focusedTender && focusedIndex < filteredTenders.length - 1) {
            e.preventDefault();
            setFocusedIndex((i) => i + 1);
          }
          break;
        case "n":
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((i) => Math.min(i + 1, filteredTenders.length - 1));
          break;
        case "p":
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((i) => Math.max(i - 1, 0));
          break;
        case "Escape":
          e.preventDefault();
          setExpandedTenderId(null);
          setSelectedTender(null);
          setShowShortcutHelp(false);
          setShowBulkConfirm(false);
          break;
        case "?":
          e.preventDefault();
          setShowShortcutHelp((v) => !v);
          break;
        case "/":
          e.preventDefault();
          document.querySelector<HTMLInputElement>('input[aria-label="Rechercher un appel d\'offres"]')?.focus();
          break;
        case "x":
          if (focusedTender) {
            e.preventDefault();
            toggleSelect(focusedTender.id);
          }
          break;
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [focusedTender, focusedIndex, filteredTenders.length, qualificationTab]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Appels d'offres</h1>
            <p className="text-muted-foreground">
              Gérer tous les AO ingérés
              {data && (
                <span className="ml-2">
                  ({filteredTenders.length} AO{qualificationTab === "new" ? " nouveaux" : qualificationTab === "auto-qualified" ? " auto-validés" : qualificationTab === "needs-review" ? " à revoir" : qualificationTab === "approved" ? " validés" : " rejetés"})
                </span>
              )}
            </p>
            <div className="mt-2 text-sm text-muted-foreground">
              <Badge variant="outline">BOAMP = Bulletin officiel des marchés publics</Badge>
            </div>
          </div>
          {canAct && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Créer un AO
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <Tabs value={qualificationTab} onValueChange={(v) => {
              setQualificationTab(v as any);
              setPage(1);
            }}>
              <TabsList>
                <TabsTrigger value="new">
                  Nouveaux
                  {newCount > 0 && <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">{newCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="auto-qualified">
                  Auto-validés
                  {autoQualifiedCount > 0 && <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">{autoQualifiedCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="needs-review">
                  À revoir
                  {needsReviewCount > 0 && <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">{needsReviewCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="approved">
                  Validés
                  {approvedCount > 0 && <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">{approvedCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="rejected">Rejetés</TabsTrigger>
                <TabsTrigger value="invalid">
                  Invalides
                  {invalidData && invalidData.total > 0 && (
                    <Badge variant="destructive" className="ml-1.5 text-xs px-1.5 py-0">{invalidData.total}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap gap-4 items-center mt-4">
              <div className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par titre, acheteur..."
                    aria-label="Rechercher un appel d'offres"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                className="px-3 py-2 border rounded-md"
                aria-label="Filtrer par qualité"
                value={qualityFilter}
                onChange={(e) => {
                  setQualityFilter(e.target.value as "all" | "low");
                  setPage(1);
                }}
              >
                <option value="all">Tous</option>
                <option value="low">À revoir (&lt;60)</option>
              </select>
              <Input
                placeholder="Source (BOAMP, TED...)"
                aria-label="Filtrer par source"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(1);
                }}
                className="w-40"
              />
              {canAct && (
                <Button
                  variant={myAoOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMyAoOnly(!myAoOnly)}
                  className="whitespace-nowrap"
                >
                  <UserIcon className="h-4 w-4 mr-1" />
                  Mes AO
                </Button>
              )}
              {isSuperAdmin && (
                <Button
                  variant={showPerformance ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowPerformance(!showPerformance)}
                  className="whitespace-nowrap"
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Rapport OPS
                </Button>
              )}
            </div>
          </CardHeader>

          {/* OPS Performance Report (SUPER_ADMIN only) */}
          {isSuperAdmin && showPerformance && (
            <div className="px-6 pb-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Performance OPS (7 derniers jours)
                </h3>
                <Button variant="ghost" size="sm" onClick={() => refetchPerformance()}>
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
              {opsPerformance && opsPerformance.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Opérateur</TableHead>
                      <TableHead className="text-center">Qualifiés</TableHead>
                      <TableHead className="text-center">Rejetés</TableHead>
                      <TableHead className="text-center">Total traités</TableHead>
                      <TableHead className="text-center">Taux rejet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {opsPerformance.map((op: any) => (
                      <TableRow key={op.userId}>
                        <TableCell className="font-medium">{op.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="default" className="text-xs">{op.qualified}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="destructive" className="text-xs">{op.rejected}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{op.total}</TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={op.rejectionRate > 70 ? "text-red-600 border-red-300" : op.rejectionRate > 40 ? "text-yellow-600 border-yellow-300" : "text-green-600 border-green-300"}
                          >
                            {op.rejectionRate}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune donnée de performance sur les 7 derniers jours.</p>
              )}
            </div>
          )}

          {(qualificationTab === "auto-qualified" || qualificationTab === "approved") && autoQualifiedCount + approvedCount > 0 && (
            <div className="px-6 pb-3 flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-blue-500" />
              <span className="text-muted-foreground">
                {approvedCount > 0 && autoQualifiedCount + approvedCount > 0
                  ? `${Math.round((approvedCount / (autoQualifiedCount + approvedCount)) * 100)}% des AO auto-validés confirmés sans override (page courante)`
                  : "Aucun AO confirmé sur cette page"}
              </span>
            </div>
          )}

          <CardContent>
            {qualificationTab === "invalid" ? (
              invalidLoading ? (
                <ListPageSkeleton />
              ) : invalidError ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                  <AlertCircle className="h-12 w-12 text-destructive/40" />
                  <p className="text-muted-foreground font-medium">Erreur de chargement des AO invalides</p>
                  <Button variant="outline" onClick={() => refetchInvalid()}>
                    <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                  </Button>
                </div>
              ) : !invalidData?.items?.length ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-400/40 mx-auto mb-3" />
                  <p className="text-muted-foreground">Aucun AO invalide</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Titre</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Date ingestion</TableHead>
                        <TableHead>Type d'erreur</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invalidData.items.map((tender: any) => (
                        <TableRow key={tender.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium max-w-sm truncate">{tender.title || tender.sourceId || "Sans titre"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{tender.source}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(tender.createdAt)}</TableCell>
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="destructive" className="cursor-help max-w-[200px] truncate">
                                    {tender.invalidReason || "Erreur de parsing"}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-md">
                                  <p className="font-medium mb-1">Détails de l'erreur</p>
                                  <p className="text-xs">{tender.invalidReason || "Aucune raison spécifiée"}</p>
                                  {tender.parsingErrors && (
                                    <pre className="text-xs mt-1 max-h-40 overflow-auto whitespace-pre-wrap">
                                      {typeof tender.parsingErrors === "string"
                                        ? tender.parsingErrors
                                        : JSON.stringify(tender.parsingErrors, null, 2)}
                                    </pre>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {canAct && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (confirm("Relancer le parsing de cet AO ?")) {
                                        retryParsingMutation.mutate({ tenderId: tender.id });
                                      }
                                    }}
                                    disabled={retryParsingMutation.isPending}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                    Retry
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => {
                                      if (confirm("Marquer cet AO comme invalide définitif ? Il ne pourra plus être retraité.")) {
                                        markInvalidMutation.mutate({
                                          id: tender.id,
                                          invalidReason: "Marqué invalide définitif par l'opérateur",
                                        });
                                      }
                                    }}
                                    disabled={markInvalidMutation.isPending}
                                  >
                                    <Ban className="h-3.5 w-3.5 mr-1" />
                                    Définitif
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {invalidData.total > 20 && (
                    <div className="flex items-center justify-between border-t pt-4 mt-4">
                      <div className="text-sm text-muted-foreground">
                        {(page - 1) * 20 + 1} à {Math.min(page * 20, invalidData.total)} sur {invalidData.total} AO invalides
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="h-4 w-4 mr-1" />Précédent
                        </Button>
                        <div className="text-sm">Page {page} sur {Math.ceil(invalidData.total / 20)}</div>
                        <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil(invalidData.total / 20)}>
                          Suivant<ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            ) : isLoading ? (
              <ListPageSkeleton />
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" aria-hidden="true" />
                <p className="text-muted-foreground font-medium">Erreur de chargement des AO</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />Réessayer
                </Button>
              </div>
            ) : filteredTenders.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" aria-hidden="true" />
                <p className="text-muted-foreground">Aucun AO trouvé</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {canAct && qualificationTab === "new" && (
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={filteredTenders.length > 0 && filteredTenders.every((t: any) => selectedIds.has(t.id))}
                          onChange={toggleSelectAll}
                          aria-label="Tout sélectionner"
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </TableHead>
                    )}
                    <TableHead>Titre</TableHead>
                    <SortableHead field="score" label="Score" sortField={sortField} sortDir={sortDir} onSort={(f, d) => { setSortField(f); setSortDir(d); }} />
                    <TableHead>Acheteur</TableHead>
                    <TableHead>Location</TableHead>
                    <SortableHead field="deadline" label="Deadline" sortField={sortField} sortDir={sortDir} onSort={(f, d) => { setSortField(f); setSortDir(d); }} />
                    <TableHead>Source</TableHead>
                    <TableHead>Qualification</TableHead>
                    <TableHead>Catégories</TableHead>
                    {isSuperAdmin && <TableHead>Assigné à</TableHead>}
                    <SortableHead field="matches" label="Matches" sortField={sortField} sortDir={sortDir} onSort={(f, d) => { setSortField(f); setSortDir(d); }} />
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody ref={tableRef}>
                  {filteredTenders.map((tender: any, idx: number) => {
                    const qualStatus = tender.TenderQualification?.status || "NEW";
                    const hasSuggestions = (tender.TenderCategorySuggestion?.length || 0) > 0;
                    const isExpanded = expandedTenderId === tender.id;
                    const isFocused = focusedIndex === idx;
                    const score = tender.qualityScore ?? 0;
                    const daysUntilDeadline = tender.deadlineAt
                      ? Math.ceil((new Date(tender.deadlineAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                      : null;
                    const isDeadlineSoon = daysUntilDeadline !== null && daysUntilDeadline < 7 && daysUntilDeadline >= 0;
                    const isDeadlinePassed = daysUntilDeadline !== null && daysUntilDeadline < 0;
                    return (
                      <Fragment key={tender.id}>
                        <TableRow
                          className={`hover:bg-muted/50 transition-colors duration-200 cursor-pointer ${isFocused ? "ring-2 ring-primary ring-inset bg-primary/5" : ""} ${selectedIds.has(tender.id) ? "bg-primary/10" : ""} ${isDeadlinePassed ? "opacity-50" : isDeadlineSoon ? "bg-yellow-50/50" : ""}`}
                          onClick={() => setFocusedIndex(idx)}
                        >
                          {canAct && qualificationTab === "new" && (
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(tender.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleSelect(tender.id);
                                }}
                                aria-label={`Sélectionner ${tender.title}`}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium max-w-xs truncate">{tender.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={score >= 70 ? "bg-green-100 text-green-800 border-green-300" : score >= 40 ? "bg-yellow-100 text-yellow-800 border-yellow-300" : "bg-gray-100 text-gray-600 border-gray-300"}>
                              {score}
                            </Badge>
                          </TableCell>
                          <TableCell>{tender.buyer || "Non spécifié"}</TableCell>
                          <TableCell>{tender.location && tender.location !== "-" ? tender.location : "-"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {formatDate(tender.deadlineAt)}
                              {isDeadlineSoon && <Clock className="h-3 w-3 text-yellow-600" />}
                              {isDeadlinePassed && <XCircle className="h-3 w-3 text-red-400" />}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{tender.source}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={QUALIFICATION_STATUS_CONFIG[qualStatus]?.variant || "outline"}>
                              {QUALIFICATION_STATUS_CONFIG[qualStatus]?.label || qualStatus}
                            </Badge>
                            {(qualStatus === "QUALIFIED" || qualStatus === "APPROVED" || qualStatus === "AUTO_QUALIFIED") && tender.TenderQualification?.isRelevantForSME && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="ml-1 text-xs cursor-help">PME</Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-xs">Pertinent PME : budget &lt;2M€, durée &lt;12 mois, accessible aux entreprises 10-50 salariés.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </TableCell>
                          <TableCell>
                            {tender.TenderQualification?.Categories?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {tender.TenderQualification.TenderQualificationCategory.slice(0, 2).map((qc: any) => (
                                  <Badge key={qc.CompanyCategory.id} variant="secondary" className="text-xs">
                                    {qc.CompanyCategory.label}
                                  </Badge>
                                ))}
                                {tender.TenderQualification.TenderQualificationCategory.length > 2 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{tender.TenderQualification.TenderQualificationCategory.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          {isSuperAdmin && (
                            <TableCell>
                              {tender.TenderQualification?.assigneeUserId ? (
                                <Badge
                                  variant="outline"
                                  className="text-xs cursor-pointer hover:bg-muted/80"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReassignTenderId(tender.id);
                                  }}
                                >
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  {(() => {
                                    const s = staffList?.find((u: any) => u.id === tender.TenderQualification?.assigneeUserId);
                                    return s ? (s.firstName || s.lastName ? `${s.firstName || ""} ${s.lastName || ""}`.trim() : s.email) : "Assigné";
                                  })()}
                                </Badge>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-auto py-1 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReassignTenderId(tender.id);
                                  }}
                                >
                                  <UserIcon className="h-3 w-3 mr-1" />
                                  Assigner
                                </Button>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {(qualStatus === "QUALIFIED" || qualStatus === "APPROVED" || qualStatus === "AUTO_QUALIFIED") ? (
                              <Badge
                                variant="outline"
                                className="cursor-pointer hover:bg-muted/80 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if ((tender._count?.TenderMatch || 0) > 0) {
                                    setMatchDialogTenderId(tender.id);
                                  }
                                }}
                              >
                                <Building2 className="h-3 w-3 mr-1" />
                                {tender._count?.TenderMatch || 0} entreprises
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {hasSuggestions && qualificationTab === "new" && (
                                <Badge variant="outline" className="text-xs text-green-600">
                                  <Zap className="h-3 w-3 mr-1" />
                                  <kbd className="text-[10px] font-mono">J</kbd>
                                </Badge>
                              )}
                              {canAct && qualificationTab === "auto-qualified" && qualStatus === "AUTO_QUALIFIED" && (
                                <>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowDowngradeConfirm(tender.id);
                                          }}
                                          disabled={downgradeMutation.isPending}
                                        >
                                          <AlertTriangle className="h-3.5 w-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Downgrader → À revoir</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-green-600 border-green-300 hover:bg-green-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            saveQualificationMutation.mutate({
                                              tenderId: tender.id,
                                              status: "APPROVED",
                                              zones: tender.TenderQualification?.zones || [],
                                              isRelevantForSME: tender.TenderQualification?.isRelevantForSME ?? true,
                                              categoryIds: tender.TenderQualification?.TenderQualificationCategory?.map((c: any) => c.CompanyCategory?.id).filter(Boolean) || [],
                                            });
                                          }}
                                          disabled={saveQualificationMutation.isPending}
                                        >
                                          <ShieldCheck className="h-3.5 w-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Approuver</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </>
                              )}
                              {canAct && qualificationTab === "needs-review" && (qualStatus === "NEEDS_REVIEW" || qualStatus === "IN_REVIEW") && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-green-600 border-green-300 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          saveQualificationMutation.mutate({
                                            tenderId: tender.id,
                                            status: "APPROVED",
                                            zones: tender.TenderQualification?.zones || [],
                                            isRelevantForSME: tender.TenderQualification?.isRelevantForSME ?? true,
                                            categoryIds: tender.TenderQualification?.TenderQualificationCategory?.map((c: any) => c.CompanyCategory?.id).filter(Boolean) || [],
                                          });
                                        }}
                                        disabled={saveQualificationMutation.isPending}
                                      >
                                        <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                                        Approuver
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Valider et lancer le matching</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={isExpanded ? "Replier" : "Développer qualification"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isExpanded) {
                                    setExpandedTenderId(null);
                                    setSelectedTender(null);
                                  } else {
                                    setExpandedTenderId(tender.id);
                                    setSelectedTender(tender.id);
                                    setFocusedIndex(idx);
                                  }
                                }}
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="Voir les détails (dialog)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTenderId(null);
                                  setSelectedTender(tender.id);
                                }}
                              >
                                <Eye className="h-4 w-4" aria-hidden="true" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${tender.id}-expand`}>
                            <TableCell colSpan={(canAct && qualificationTab === "new" ? 12 : 11) + (isSuperAdmin ? 1 : 0)} className="p-0">
                              <InlineQualificationPanel
                                tenderId={tender.id}
                                categories={categories || []}
                                canAct={canAct}
                                onClose={() => {
                                  setExpandedTenderId(null);
                                  setSelectedTender(null);
                                }}
                                onNext={() => {
                                  if (idx < filteredTenders.length - 1) {
                                    const next = filteredTenders[idx + 1];
                                    setExpandedTenderId(next.id);
                                    setSelectedTender(next.id);
                                    setFocusedIndex(idx + 1);
                                  }
                                }}
                                onPrev={() => {
                                  if (idx > 0) {
                                    const prev = filteredTenders[idx - 1];
                                    setExpandedTenderId(prev.id);
                                    setSelectedTender(prev.id);
                                    setFocusedIndex(idx - 1);
                                  }
                                }}
                                refetchList={refetch}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* Pagination */}
            {totalFiltered > 0 && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="text-sm text-muted-foreground">
                  Affichage de {(page - 1) * 20 + 1} à {Math.min(page * 20, totalFiltered)} sur{" "}
                  {totalFiltered} AO
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Précédent
                  </Button>
                  <div className="text-sm">
                    Page {page} sur {Math.ceil(totalFiltered / 20)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= Math.ceil(totalFiltered / 20) || isLoading}
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Floating bulk action bar */}
        {selectedIds.size > 0 && canAct && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-primary text-primary-foreground shadow-lg rounded-lg px-6 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4">
            <span className="font-medium">{selectedIds.size} AO sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowBulkConfirm(true)}
              disabled={bulkQualifyMutation.isPending}
            >
              <Zap className="h-4 w-4 mr-1" />
              Qualifier en 1 clic
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:text-primary-foreground/80"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Bulk confirm dialog */}
        <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la qualification en masse</DialogTitle>
              <DialogDescription>
                Qualifier {selectedIds.size} AO en 1 clic. Les AO sans suggestions (score &ge; 60) seront ignorés.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => bulkQualifyMutation.mutate({ tenderIds: Array.from(selectedIds) })}
                disabled={bulkQualifyMutation.isPending}
              >
                {bulkQualifyMutation.isPending ? "Qualification..." : `Qualifier ${selectedIds.size} AO`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Downgrade AUTO_QUALIFIED confirmation */}
        <Dialog open={!!showDowngradeConfirm} onOpenChange={(open) => { if (!open) setShowDowngradeConfirm(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Annuler la validation automatique ?
              </DialogTitle>
              <DialogDescription>
                Vous allez annuler la validation automatique de cet AO. Il sera rétrogradé vers « À revoir » et ses matches seront masqués jusqu'à validation manuelle.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDowngradeConfirm(null)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (showDowngradeConfirm) {
                    downgradeMutation.mutate({ tenderId: showDowngradeConfirm });
                  }
                }}
                disabled={downgradeMutation.isPending}
              >
                {downgradeMutation.isPending ? "Rétrogradation..." : "Confirmer le downgrade"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Keyboard shortcut help */}
        <Dialog open={showShortcutHelp} onOpenChange={setShowShortcutHelp}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Raccourcis clavier
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {[
                ["J / Enter", "Qualifier en 1-clic (si suggestions OK)"],
                ["R", "Développer la ligne pour qualifier / rejeter"],
                ["S", "Passer au suivant sans action"],
                ["N / ↓", "AO suivant"],
                ["P / ↑", "AO précédent"],
                ["X", "Sélectionner / désélectionner"],
                ["/", "Focus recherche"],
                ["Esc", "Fermer panel / dialog"],
                ["?", "Afficher / masquer ce panneau"],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center gap-3">
                  <kbd className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 bg-muted rounded border text-xs font-mono">{key}</kbd>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Shortcut help toggle button */}
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-6 right-6 z-40"
          onClick={() => setShowShortcutHelp(true)}
          aria-label="Raccourcis clavier"
        >
          <Keyboard className="h-4 w-4 mr-1" />
          <kbd className="text-xs font-mono">?</kbd>
        </Button>

        {/* Dialog Créer un AO */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un appel d'offres</DialogTitle>
              <DialogDescription>
                Créer manuellement un AO (source: MANUAL). Il sera en statut IN_REVIEW.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  placeholder="Titre de l'appel d'offres"
                />
              </div>

              <div>
                <Label>Acheteur</Label>
                <Input
                  value={createForm.buyer}
                  onChange={(e) => setCreateForm({ ...createForm, buyer: e.target.value })}
                  placeholder="Nom de l'acheteur"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Description de l'AO"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Localisation</Label>
                  <Input
                    value={createForm.location}
                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                    placeholder="Ex: Paris 75"
                  />
                </div>
                <div>
                  <Label>Région</Label>
                  <Input
                    value={createForm.region}
                    onChange={(e) => setCreateForm({ ...createForm, region: e.target.value })}
                    placeholder="Ex: Île-de-France"
                  />
                </div>
              </div>

              <div>
                <Label>Date limite *</Label>
                <Input
                  type="datetime-local"
                  value={createForm.deadlineAt}
                  onChange={(e) => setCreateForm({ ...createForm, deadlineAt: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Budget min (€)</Label>
                  <Input
                    type="number"
                    value={createForm.budgetMin}
                    onChange={(e) => setCreateForm({ ...createForm, budgetMin: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Budget max (€)</Label>
                  <Input
                    type="number"
                    value={createForm.budgetMax}
                    onChange={(e) => setCreateForm({ ...createForm, budgetMax: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>URL source</Label>
                <Input
                  value={createForm.url}
                  onChange={(e) => setCreateForm({ ...createForm, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label>Catégories</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(categories || []).map((cat: any) => (
                    <Badge
                      key={cat.id}
                      variant={createForm.categoryIds.includes(cat.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        setCreateForm({
                          ...createForm,
                          categoryIds: createForm.categoryIds.includes(cat.id)
                            ? createForm.categoryIds.filter((id) => id !== cat.id)
                            : [...createForm.categoryIds, cat.id],
                        });
                      }}
                    >
                      {cat.label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Zones (départements, ex: 75, 92, 93)</Label>
                <Input
                  value={createForm.zones}
                  onChange={(e) => setCreateForm({ ...createForm, zones: e.target.value })}
                  placeholder="75, 92, 93"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createIsRelevantForSME"
                  checked={createForm.isRelevantForSME}
                  onChange={(e) => setCreateForm({ ...createForm, isRelevantForSME: e.target.checked })}
                />
                <Label htmlFor="createIsRelevantForSME">Pertinent pour PME</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-xs">Un AO est pertinent PME si : budget &lt;2M€, durée &lt;12 mois, lot non réservé aux grands groupes, exigences accessibles aux entreprises 10-50 salariés.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => {
                  if (!createForm.title || !createForm.deadlineAt) {
                    toast.error("Le titre et la date limite sont obligatoires");
                    return;
                  }
                  createTenderMutation.mutate({
                    title: createForm.title,
                    buyer: createForm.buyer || undefined,
                    description: createForm.description || undefined,
                    location: createForm.location || undefined,
                    region: createForm.region || undefined,
                    deadlineAt: new Date(createForm.deadlineAt),
                    budgetMin: createForm.budgetMin ? parseFloat(createForm.budgetMin) : undefined,
                    budgetMax: createForm.budgetMax ? parseFloat(createForm.budgetMax) : undefined,
                    url: createForm.url || undefined,
                    categoryIds: createForm.categoryIds.length > 0 ? createForm.categoryIds : undefined,
                    zones: createForm.zones ? createForm.zones.split(",").map((z) => z.trim()).filter(Boolean) : undefined,
                    isRelevantForSME: createForm.isRelevantForSME,
                  });
                }}
                disabled={createTenderMutation.isPending}
              >
                {createTenderMutation.isPending ? "Création..." : "Créer l'AO"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Réassigner un AO (SUPER_ADMIN) */}
        {isSuperAdmin && (
          <Dialog open={!!reassignTenderId} onOpenChange={(open) => { if (!open) { setReassignTenderId(null); setReassignUserId(""); } }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Réassigner l'AO
                </DialogTitle>
                <DialogDescription>
                  Sélectionner un opérateur OPS pour assigner cet AO.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Opérateur</Label>
                  <Select value={reassignUserId} onValueChange={setReassignUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un opérateur..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(staffList || [])
                        .filter((s: any) => s.role === "ADMIN_OPS" || s.role === "SUPER_ADMIN")
                        .map((s: any) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.firstName || s.lastName ? `${s.firstName || ""} ${s.lastName || ""}`.trim() : s.email}
                            <span className="text-muted-foreground ml-1 text-xs">({s.role})</span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReassignTenderId(null); setReassignUserId(""); }}>
                  Annuler
                </Button>
                <Button
                  onClick={() => {
                    if (reassignTenderId && reassignUserId) {
                      reassignMutation.mutate({ tenderId: reassignTenderId, opsUserId: reassignUserId });
                    }
                  }}
                  disabled={!reassignUserId || reassignMutation.isPending}
                >
                  {reassignMutation.isPending ? "Réassignation..." : "Réassigner"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Dialog Résultats matching */}
        <Dialog open={!!matchDialogTenderId} onOpenChange={(open) => !open && setMatchDialogTenderId(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Entreprises matchées
              </DialogTitle>
              <DialogDescription>
                {matchDialogLoading ? (
                  <Skeleton className="h-4 w-48" />
                ) : matchDialogDetail ? (
                  <span>{matchDialogDetail.title} — {((matchDialogDetail as any).TenderMatch || []).length} match{((matchDialogDetail as any).TenderMatch || []).length !== 1 ? "es" : ""}</span>
                ) : "Détails des entreprises correspondantes"}
              </DialogDescription>
            </DialogHeader>
            {matchDialogLoading ? (
              <div className="space-y-3 mt-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : matchDialogDetail && ((matchDialogDetail as any).TenderMatch || []).length > 0 ? (
              <div className="space-y-3 mt-4">
                {((matchDialogDetail as any).TenderMatch || []).map((match: any) => (
                  <div key={match.id} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{match.Company?.name || "Entreprise inconnue"}</span>
                        <Badge
                          variant={match.status === "VISIBLE" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {match.status === "VISIBLE" ? "Visible" : "Masqué"}
                        </Badge>
                        {match.recommended && (
                          <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50">
                            Recommandé
                          </Badge>
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={match.score >= 70 ? "bg-green-100 text-green-800 border-green-300" : match.score >= 40 ? "bg-yellow-100 text-yellow-800 border-yellow-300" : "bg-gray-100 text-gray-600 border-gray-300"}
                      >
                        Score: {match.score}
                      </Badge>
                    </div>
                    {match.reasons && (
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {typeof match.reasons === "object" && !Array.isArray(match.reasons) ? (
                          Object.entries(match.reasons as Record<string, unknown>).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-1">
                              <span className="font-medium capitalize">{key.replace(/([A-Z])/g, " $1").trim()}:</span>
                              <span>{String(val)}</span>
                            </div>
                          ))
                        ) : Array.isArray(match.reasons) ? (
                          (match.reasons as string[]).map((r: string, idx: number) => (
                            <div key={idx}>• {r}</div>
                          ))
                        ) : (
                          <div>{String(match.reasons)}</div>
                        )}
                      </div>
                    )}
                    {match.Company?.id && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 mt-1 text-xs"
                        onClick={() => navigate(`/companies/${match.Company.id}`)}
                      >
                        Voir la fiche entreprise →
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">Aucun match trouvé</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Fiche AO avec Qualification */}
        <Dialog open={!!selectedTender} onOpenChange={(open) => !open && setSelectedTender(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Fiche Appel d'offres</DialogTitle>
              <DialogDescription>
                {tenderDetailLoading ? <Skeleton className="h-4 w-48" /> : (tenderDetail?.title || "Détails de l'AO")}
              </DialogDescription>
            </DialogHeader>

            {tenderDetailLoading ? (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-4 w-full" />
                    ))}
                  </div>
                  <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-4 w-full" />
                    ))}
                  </div>
                </div>
              </div>
            ) : tenderDetail ? (
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-6">
                  {/* Colonne gauche : Infos AO */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Informations AO</h4>
                      <div className="space-y-1 text-sm">
                        <p>
                          <strong>Source:</strong> {tenderDetail.source}
                        </p>
                        <p>
                          <strong>Acheteur:</strong> {tenderDetail.buyer || "-"}
                        </p>
                        <p>
                          <strong>Location:</strong> {tenderDetail.location || "-"}
                        </p>
                        <p>
                          <strong>Région:</strong> {tenderDetail.region || "-"}
                        </p>
                        <p>
                          <strong>Deadline:</strong> {formatDate(tenderDetail.deadlineAt)}
                        </p>
                        <p>
                          <strong>Budget:</strong>{" "}
                          {tenderDetail.budgetMin || tenderDetail.budgetMax
                            ? `${tenderDetail.budgetMin ? `€${tenderDetail.budgetMin.toLocaleString()}` : ""} - ${tenderDetail.budgetMax ? `€${tenderDetail.budgetMax.toLocaleString()}` : ""}`
                            : "-"}
                        </p>
                      </div>
                    </div>

                    {tenderDetail.description && (
                      <div>
                        <h4 className="font-semibold mb-2">Description</h4>
                        <p className="text-sm">{tenderDetail.description}</p>
                      </div>
                    )}

                    {(tenderDetail as any).rawJson && (
                      <details className="border rounded p-3">
                        <summary className="font-semibold cursor-pointer flex items-center gap-2">
                          <FileJson className="h-4 w-4" />
                          <span>Raw JSON</span>
                        </summary>
                        <pre className="text-xs bg-muted p-3 rounded border overflow-auto max-h-96 mt-2">
                          {JSON.stringify((tenderDetail as any).rawJson, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>

                  {/* Colonne droite : Qualification */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Suggestions automatiques</h4>
                      {suggestionsLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-6 w-32" />
                          <Skeleton className="h-6 w-24" />
                        </div>
                      ) : suggestionsError ? (
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          <span>Erreur de chargement des suggestions</span>
                        </div>
                      ) : suggestions && suggestions.length > 0 ? (
                        <div className="space-y-2">
                          {suggestions.map((s: any) => (
                            <Badge key={s.id} variant="outline" className="mr-2 mb-2">
                              {s.CompanyCategory.label} ({s.score}/100)
                            </Badge>
                          ))}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (suggestions && suggestions.length > 0) {
                                  const categoryIds = suggestions.map((s: any) => s.CompanyCategory.id);
                                  // Pré-remplir le formulaire avec les suggestions
                                  // (sera géré dans le formulaire de qualification)
                                }
                              }}
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              Appliquer suggestions
                            </Button>
                            {suggestions.some((s: any) => s.score >= 60) && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  if (confirm("Valider cet AO en 1 clic avec les suggestions top 1-3 (score >= 60) ?")) {
                                    qualifyInOneClickMutation.mutate({ tenderId: tenderDetail.id });
                                  }
                                }}
                                disabled={qualifyInOneClickMutation.isPending}
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Valider en 1 clic
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">
                            Aucune suggestion disponible
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              generateSuggestionsMutation.mutate({ tenderId: tenderDetail.id });
                            }}
                            disabled={generateSuggestionsMutation.isPending}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Générer suggestions
                          </Button>
                        </div>
                      )}
                    </div>

                    {qualificationLoading ? (
                      <div className="space-y-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton key={i} className="h-8 w-full" />
                        ))}
                      </div>
                    ) : qualificationError ? (
                      <div className="flex items-center gap-2 text-sm text-destructive p-3 border border-destructive/20 rounded bg-destructive/5">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>Erreur de chargement de la qualification</span>
                        <Button variant="ghost" size="sm" onClick={() => refetchQualification()} className="ml-auto h-auto p-1">
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <QualificationForm
                        tenderId={tenderDetail.id}
                        qualification={qualification}
                        suggestions={suggestions}
                        categories={categories || []}
                        onSave={(data) => {
                          saveQualificationMutation.mutate(data);
                        }}
                        isLoading={saveQualificationMutation.isPending}
                        readOnly={!canAct}
                        qualityScore={(tenderDetail as any).qualityScore}
                      />
                    )}
                  </div>
                </div>

                {/* Matches si qualifié */}
                {qualification?.status === "QUALIFIED" &&
                  (tenderDetail as any).TenderMatch &&
                  ((tenderDetail as any).TenderMatch || []).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">
                        Matches ({((tenderDetail as any).TenderMatch || []).length} entreprises)
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {((tenderDetail as any).TenderMatch || []).map((match: any) => (
                          <div key={match.id} className="p-2 border rounded text-sm">
                            <div className="flex items-center justify-between">
                              <span>{match.Company?.name || "-"}</span>
                              <Badge variant="outline">Score: {match.score}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Actions */}
                {canAct && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Marquer cet AO comme invalide ?")) {
                          markInvalidMutation.mutate({
                            id: tenderDetail.id,
                            invalidReason: "Marqué manuellement",
                          });
                        }
                      }}
                      disabled={markInvalidMutation.isPending || tenderDetail.isInvalid}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Marquer invalide
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        markValidMutation.mutate({ id: tenderDetail.id });
                      }}
                      disabled={markValidMutation.isPending || !tenderDetail.isInvalid}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Marquer valide
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Recalculer les matches pour cet AO ?")) {
                          recalculateMatchesMutation.mutate({ tenderId: tenderDetail.id });
                        }
                      }}
                      disabled={recalculateMatchesMutation.isPending}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Recalculer matches
                    </Button>
                  </div>
                )}
              </div>
            ) : tenderDetailError ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] gap-4 mt-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" />
                <p className="text-muted-foreground font-medium">Erreur de chargement de l'AO</p>
                <Button variant="outline" onClick={() => refetchTenderDetail()}>Réessayer</Button>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

// Composant formulaire de qualification
function QualificationForm({
  tenderId,
  qualification,
  suggestions,
  categories,
  onSave,
  isLoading,
  readOnly = false,
  qualityScore,
}: {
  tenderId: string;
  qualification: any;
  suggestions: any;
  categories: any[];
  onSave: (data: any) => void;
  isLoading: boolean;
  readOnly?: boolean;
  qualityScore?: number;
}) {
  const [status, setStatus] = useState<string>(qualification?.status || "NEW");
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    qualification?.TenderQualificationCategory?.map((c: any) => c.CompanyCategory.id) || []
  );
  const [zones, setZones] = useState<string[]>(qualification?.zones || []);
  const [estimatedBudgetMin, setEstimatedBudgetMin] = useState<number | undefined>(
    qualification?.estimatedBudgetMin || undefined
  );
  const [estimatedBudgetMax, setEstimatedBudgetMax] = useState<number | undefined>(
    qualification?.estimatedBudgetMax || undefined
  );
  const [complexity, setComplexity] = useState<string>(qualification?.complexity || "");
  const [isRelevantForSME, setIsRelevantForSME] = useState<boolean>(
    qualification?.isRelevantForSME ?? true
  );
  const [notes, setNotes] = useState<string>(qualification?.notes || "");
  const [rejectionReason, setRejectionReason] = useState<string>(qualification?.rejectionReason || "");
  const [showZeroMatchConfirm, setShowZeroMatchConfirm] = useState(false);

  // Debounced preview matching
  const [previewInput, setPreviewInput] = useState<{
    tenderId: string;
    categoryIds: string[];
    zones: string[];
    estimatedBudgetMin?: number;
    estimatedBudgetMax?: number;
  } | null>(null);

  useEffect(() => {
    if (selectedCategories.length === 0) {
      setPreviewInput(null);
      return;
    }
    const timer = setTimeout(() => {
      setPreviewInput({
        tenderId,
        categoryIds: selectedCategories,
        zones,
        estimatedBudgetMin,
        estimatedBudgetMax,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [tenderId, selectedCategories, zones, estimatedBudgetMin, estimatedBudgetMax]);

  const { data: matchPreview, isLoading: previewLoading } = trpc.admin.qualification.previewMatching.useQuery(
    previewInput!,
    { enabled: !!previewInput && previewInput.categoryIds.length > 0 }
  );

  // Reset state when qualification or tenderId changes
  useEffect(() => {
    setStatus(qualification?.status || "NEW");
    setSelectedCategories(qualification?.TenderQualificationCategory?.map((c: any) => c.CompanyCategory.id) || []);
    setZones(qualification?.zones || []);
    setEstimatedBudgetMin(qualification?.estimatedBudgetMin || undefined);
    setEstimatedBudgetMax(qualification?.estimatedBudgetMax || undefined);
    setComplexity(qualification?.complexity || "");
    setIsRelevantForSME(qualification?.isRelevantForSME ?? true);
    setNotes(qualification?.notes || "");
    setRejectionReason(qualification?.rejectionReason || "");
  }, [tenderId, qualification]);

  const handleApplySuggestions = () => {
    if (suggestions && suggestions.length > 0) {
      const suggestedIds = suggestions.map((s: any) => s.CompanyCategory.id);
      setSelectedCategories([...new Set([...selectedCategories, ...suggestedIds])]);
    }
  };

  const handleSave = (finalStatus: string) => {
    if (finalStatus === "QUALIFIED" && selectedCategories.length === 0) {
      toast.error("Sélectionnez au moins une catégorie pour qualifier");
      return;
    }

    if (finalStatus === "QUALIFIED" && !isRelevantForSME) {
      toast.error("Un AO qualifié doit être pertinent pour les PME");
      return;
    }

    if (finalStatus === "QUALIFIED" && matchPreview && matchPreview.matchedCount === 0 && !showZeroMatchConfirm) {
      setShowZeroMatchConfirm(true);
      return;
    }

    setShowZeroMatchConfirm(false);
    onSave({
      tenderId,
      status: finalStatus,
      categoryIds: selectedCategories,
      zones,
      estimatedBudgetMin,
      estimatedBudgetMax,
      complexity: complexity || undefined,
      isRelevantForSME,
      notes,
      rejectionReason: finalStatus === "REJECTED" ? rejectionReason : undefined,
    });
  };

  return (
    <div className="space-y-4 border rounded-lg p-4">
      <h4 className="font-semibold">Qualification Ops</h4>

      <div>
        <Label>Statut</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NEW">À qualifier</SelectItem>
            <SelectItem value="IN_REVIEW">En cours</SelectItem>
            <SelectItem value="QUALIFIED">Qualifié</SelectItem>
            <SelectItem value="REJECTED">Rejeté</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Catégories *</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {categories.map((cat) => (
            <Badge
              key={cat.id}
              variant={selectedCategories.includes(cat.id) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => {
                if (selectedCategories.includes(cat.id)) {
                  setSelectedCategories(selectedCategories.filter((id) => id !== cat.id));
                } else {
                  setSelectedCategories([...selectedCategories, cat.id]);
                }
              }}
            >
              {cat.label}
            </Badge>
          ))}
        </div>
        {suggestions && suggestions.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={handleApplySuggestions}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Appliquer suggestions
          </Button>
        )}
      </div>

      <div>
        <Label>Zones (départements)</Label>
        <ZoneMultiSelect value={zones} onChange={setZones} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Budget min (€)</Label>
          <Input
            type="number"
            value={estimatedBudgetMin || ""}
            onChange={(e) => {
              setEstimatedBudgetMin(e.target.value ? parseFloat(e.target.value) : undefined);
            }}
          />
        </div>
        <div>
          <Label>Budget max (€)</Label>
          <Input
            type="number"
            value={estimatedBudgetMax || ""}
            onChange={(e) => {
              setEstimatedBudgetMax(e.target.value ? parseFloat(e.target.value) : undefined);
            }}
          />
        </div>
      </div>

      <div>
        <Label>Complexité</Label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={complexity}
          onChange={(e) => setComplexity(e.target.value)}
        >
          <option value="">Sélectionner</option>
          <option value="LOW">Faible</option>
          <option value="MEDIUM">Moyenne</option>
          <option value="HIGH">Élevée</option>
        </select>
      </div>

      <div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isRelevantForSME"
            checked={isRelevantForSME}
            onChange={(e) => setIsRelevantForSME(e.target.checked)}
          />
          <Label htmlFor="isRelevantForSME">Pertinent pour PME</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-xs">Un AO est pertinent PME si : budget &lt;2M€, durée &lt;12 mois, lot non réservé aux grands groupes, exigences accessibles aux entreprises 10-50 salariés.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {!isRelevantForSME && (
          <div className="flex items-center gap-2 mt-1.5 p-2 rounded bg-amber-50 border border-amber-200 text-amber-700 text-xs">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Cet AO ne sera diffusé à aucune entreprise même si qualifié
          </div>
        )}
      </div>

      <div>
        <Label>Notes</Label>
        <textarea
          className="w-full p-2 border rounded-md text-sm"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes internes..."
        />
      </div>

      {status === "REJECTED" && (
        <div>
          <Label>Raison du rejet *</Label>
          <textarea
            className="w-full p-2 border rounded-md text-sm"
            rows={2}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Pourquoi cet AO est rejeté ?"
          />
        </div>
      )}

      {/* Quality warning */}
      {qualityScore !== undefined && qualityScore < 30 && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>AO de faible qualité (score {qualityScore}) — pertinence à vérifier</span>
        </div>
      )}

      {/* Preview matching panel */}
      {selectedCategories.length > 0 && (
        <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-sm space-y-1">
          <div className="flex items-center gap-2 font-medium text-blue-800">
            <Sparkles className="h-4 w-4" />
            <span>Cette qualification va matcher :</span>
            {previewLoading && <RefreshCw className="h-3 w-3 animate-spin" />}
          </div>
          {matchPreview ? (
            <ul className="list-disc list-inside text-blue-700 space-y-0.5 ml-1">
              <li>
                <strong>{matchPreview.matchedCount}</strong> entreprise{matchPreview.matchedCount !== 1 ? "s" : ""}
                {matchPreview.recommendedCount > 0 && (
                  <span> ({matchPreview.recommendedCount} recommandée{matchPreview.recommendedCount !== 1 ? "s" : ""}, score &ge; 70)</span>
                )}
              </li>
              <li>
                <strong>{matchPreview.zoneCount}</strong> entreprise{matchPreview.zoneCount !== 1 ? "s" : ""} dans la zone {zones.length > 0 ? zones.join(", ") : "(aucune zone)"}
              </li>
              <li>
                <strong>{matchPreview.budgetCompatibleCount}</strong> entreprise{matchPreview.budgetCompatibleCount !== 1 ? "s" : ""} avec budget compatible
              </li>
            </ul>
          ) : !previewLoading ? (
            <p className="text-blue-600 text-xs">Modifiez catégories/zones/budget pour voir l'aperçu</p>
          ) : null}
        </div>
      )}

      {/* Zero match warning */}
      {matchPreview && matchPreview.matchedCount === 0 && selectedCategories.length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-300 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Aucune entreprise ne correspond à ces critères. Vérifiez les catégories et zones.</span>
        </div>
      )}

      {/* Zero match double confirmation */}
      {showZeroMatchConfirm && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-red-100 border border-red-400 text-red-800 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>0 match détecté. Êtes-vous sûr de vouloir valider ?</span>
          <Button size="sm" variant="destructive" onClick={() => handleSave("QUALIFIED")} disabled={isLoading}>
            Confirmer
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowZeroMatchConfirm(false)}>
            Annuler
          </Button>
        </div>
      )}

      {!readOnly && (
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave("IN_REVIEW")}
            disabled={isLoading}
          >
            Enregistrer (brouillon)
          </Button>
          {status === "QUALIFIED" || selectedCategories.length > 0 ? (
            <Button
              size="sm"
              onClick={() => handleSave("QUALIFIED")}
              disabled={isLoading || selectedCategories.length === 0 || !isRelevantForSME}
              title={selectedCategories.length === 0 ? "Sélectionnez au moins une catégorie" : undefined}
            >
              <Check className="h-4 w-4 mr-1" />
              Valider et diffuser
            </Button>
          ) : (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleSave("REJECTED")}
              disabled={isLoading || !rejectionReason}
            >
              <X className="h-4 w-4 mr-1" />
              Rejeter
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function InlineQualificationPanel({
  tenderId,
  categories,
  canAct,
  onClose,
  onNext,
  onPrev,
  refetchList,
}: {
  tenderId: string;
  categories: any[];
  canAct: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  refetchList: () => void;
}) {
  const { data: qualification, isLoading: qualLoading, refetch: refetchQual } = trpc.admin.qualification.get.useQuery(
    { tenderId },
    { enabled: !!tenderId }
  );
  const { data: suggestions, isLoading: sugLoading } = trpc.admin.tenders.getSuggestions.useQuery(
    { tenderId },
    { enabled: !!tenderId }
  );

  const qualifyOneClick = trpc.admin.qualification.qualifyInOneClick.useMutation({
    onSuccess: (result: { matchedCompanies: number }) => {
      toast.success(`Qualifié ! ${result.matchedCompanies} entreprises matchées`);
      refetchQual();
      refetchList();
      onNext();
    },
    onError: (err: { message: string }) => toast.error(err.message),
  });

  const saveMut = trpc.admin.qualification.createOrUpdate.useMutation({
    onSuccess: () => {
      toast.success("Qualification enregistrée");
      refetchQual();
      refetchList();
    },
    onError: (err: { message: string }) => toast.error(err.message),
  });

  if (qualLoading || sugLoading) {
    return (
      <div className="p-4 bg-muted/30 border-t border-b">
        <div className="flex gap-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
    );
  }

  const hasSuggestions = suggestions && suggestions.some((s: any) => s.score >= 60);

  return (
    <div className="p-4 bg-muted/30 border-t border-b space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h5 className="font-semibold text-sm">Qualification rapide</h5>
          {suggestions && suggestions.length > 0 && (
            <div className="flex gap-1">
              {suggestions.slice(0, 3).map((s: any) => (
                <Badge key={s.id} variant="outline" className="text-xs">
                  {s.CompanyCategory.label} ({s.score})
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onPrev} aria-label="AO précédent">
            <ChevronLeft className="h-4 w-4" />
            <kbd className="text-[10px] font-mono ml-0.5">P</kbd>
          </Button>
          <Button variant="ghost" size="sm" onClick={onNext} aria-label="AO suivant">
            <kbd className="text-[10px] font-mono mr-0.5">N</kbd>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {hasSuggestions && (
          <Button
            size="sm"
            onClick={() => qualifyOneClick.mutate({ tenderId })}
            disabled={qualifyOneClick.isPending}
          >
            <Zap className="h-3 w-3 mr-1" />
            Qualifier en 1 clic
            <kbd className="ml-1 text-[10px] font-mono bg-primary-foreground/20 px-1 rounded">J</kbd>
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onNext}
        >
          Passer
          <kbd className="ml-1 text-[10px] font-mono">S</kbd>
        </Button>
      </div>

      {canAct && (
        <QualificationForm
          tenderId={tenderId}
          qualification={qualification}
          suggestions={suggestions}
          categories={categories}
          onSave={(data) => saveMut.mutate(data)}
          isLoading={saveMut.isPending}
          readOnly={!canAct}
        />
      )}
    </div>
  );
}
